import React, { useState } from "react";
import "../../../../styles/gridStyle.css";
import LostAndFoundPetsGridRow from "./LostAndFoundPetsGridRow";
import { ILostAndFoundPetsResponseModel } from "../../../../typings/models/LostAndFoundPetsResponseModel";
import Loader from "../../../../components/molecules/Loader";

interface GridProps {
  data: ILostAndFoundPetsResponseModel[];
  isLoading: boolean;
  headerColumns: any[];
}

const LostAndFoundPetsGrid: React.FC<GridProps> = ({
  data,
  isLoading,
  headerColumns,
}) => {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const itemsPerPage = 10;
  const maxPagesToShow = 5;

  const filteredData =
    data.length > 0
      ? data.filter((item) =>
          Object.values(item).some((value) =>
            value?.toString().toLowerCase().includes(searchQuery?.toLowerCase())
          )
        )
      : [];

  // Function to perform sorting based on the current sort settings
  const sortedData = filteredData.sort((a, b) => {
    if (sortColumn === null) return 0;

    const aValue = a[sortColumn];
    const bValue = b[sortColumn];

    if (aValue < bValue) {
      return sortDirection === "asc" ? -1 : 1;
    } else if (aValue > bValue) {
      return sortDirection === "asc" ? 1 : -1;
    } else {
      return 0;
    }
  });

  // Calculate the total number of pages
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);

  // Get the data to display on the current page
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const dataToShow = sortedData.slice(startIndex, endIndex);
  const [activeButton, setActiveButton] = useState<string>(""); // added for edit form

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to the first page when searching
  };

  const handlePagination = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleSort = (column: string) => {
    if (column === sortColumn) {
      // If the same column is clicked again, toggle the sort direction
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // If a new column is clicked, set the new column and default to ascending sort
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const renderPageButtons = () => {
    const pageButtons = [];
    let startPage = 1;
    let endPage = totalPages;

    if (totalPages > maxPagesToShow) {
      const halfMaxPages = Math.floor(maxPagesToShow / 2);
      startPage = Math.max(currentPage - halfMaxPages, 1);
      endPage = startPage + maxPagesToShow - 1;

      if (endPage > totalPages) {
        endPage = totalPages;
        startPage = endPage - maxPagesToShow + 1;
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pageButtons.push(
        <li
          key={i}
          className={`page-item ${currentPage === i ? "active" : ""}`}
        >
          <button className="page-link" onClick={() => handlePagination(i)}>
            {i}
          </button>
        </li>
      );
    }

    if (totalPages > maxPagesToShow && endPage < totalPages) {
      pageButtons.push(
        <li key="separator" className="page-item disabled">
          <button className="page-link">...</button>
        </li>
      );
      pageButtons.push(
        <li
          key={totalPages}
          className={`page-item ${currentPage === totalPages ? "active" : ""}`}
        >
          <button
            className="page-link"
            onClick={() => handlePagination(totalPages)}
          >
            {totalPages}
          </button>
        </li>
      );
    }

    return pageButtons;
  };
  // You can add pagination, sorting, and search logic similar to PetsGrid here if needed

  return (
    <div className="container">
      <input
        type="text"
        placeholder="Search..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        className="form-control mb-3"
      />
      <div className="table-responsive">
        <table className="table table-bordered table-hover">
          <thead>
            <tr>
              {headerColumns.map((column, index) => (
                <th
                  key={index}
                  className="grid-header"
                  onClick={() => handleSort(column.sortKey)}
                >
                  {/* {column.columnName}
                {sortColumn === column.sortKey && (
                  <span>{sortDirection === "asc" ? "▲" : "▼"}</span>
                )} */}
                  {column.columnName} &nbsp;
                  <span>{sortDirection === "asc" ? "▲" : "▼"}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {dataToShow.map((item, index) => (
              <LostAndFoundPetsGridRow key={index} data={item} />
            ))}
          </tbody>
        </table>

        {isLoading && <Loader />}
      </div>
    </div>
  );
};

export default LostAndFoundPetsGrid;
